import { create } from 'zustand'
import { User } from 'firebase/auth'
import { Driver, MergedDriver, Platform } from '@/types'

/**
 * IMPORTANT: Driver Firestore Operations Removed
 * 
 * The following functionality has been temporarily removed to prepare for new driver creation logic:
 * - All driver CRUD operations (create, read, update, delete)
 * - Driver merging and unmerging functionality  
 * - Driver contact updates and tax rate settings
 * - Auto-matching drivers
 * - Batch operations
 * 
 * TODO: Implement new driver creation and management logic
 */

interface DriverStore {
  // State
  drivers: Driver[]
  mergedDrivers: MergedDriver[]
  loading: boolean
  error: string | null
  selectedDriverIds: string[]

  // Actions
  loadDrivers: (user: User) => Promise<void>
  selectDriver: (id: string) => void
  unselectDriver: (id: string) => void
  clearSelection: () => void
  toggleDriverSelection: (id: string) => void
  selectAllUnmergedDrivers: () => void
  isDriverSelected: (id: string) => boolean
  getSelectedDrivers: () => Driver[]
  getDriverById: (id: string) => Driver | MergedDriver | undefined
  getDriversByPlatform: (platform: Platform) => Driver[]
  getTotalEarnings: () => { gross: number; net: number; tips: number }
}

export const useDriverStore = create<DriverStore>((set, get) => ({
  // Initial state
  drivers: [],
  mergedDrivers: [],
  loading: false,
  error: null,
  selectedDriverIds: [],
  
  // Load drivers (placeholder implementation)
  loadDrivers: async (_user: User) => {
    set({ loading: true, error: null })
    try {
      // TODO: Implement new driver loading logic here
      // For now, clear all drivers since we're removing Firestore operations
      set({
        drivers: [],
        mergedDrivers: [],
        loading: false
      })
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'Failed to load drivers',
        loading: false 
      })
      throw error
    }
  },

  // Selection management (unchanged)
  selectDriver: (id: string) => {
    set((state) => ({
      selectedDriverIds: [...state.selectedDriverIds, id]
    }))
  },

  unselectDriver: (id: string) => {
    set((state) => ({
      selectedDriverIds: state.selectedDriverIds.filter(driverId => driverId !== id)
    }))
  },

  clearSelection: () => {
    set({ selectedDriverIds: [] })
  },

  toggleDriverSelection: (id: string) => {
    const state = get()
    if (state.selectedDriverIds.includes(id)) {
      state.unselectDriver(id)
    } else {
      state.selectDriver(id)
    }
  },

  selectAllUnmergedDrivers: () => {
    const state = get()
    const unmergedDriverIds = state.drivers
      .filter(driver => !driver.merged)
      .map(driver => driver.id)
    
    set({ selectedDriverIds: unmergedDriverIds })
  },

  isDriverSelected: (id: string) => {
    return get().selectedDriverIds.includes(id)
  },

  getSelectedDrivers: () => {
    const state = get()
    return state.drivers.filter(driver => 
      state.selectedDriverIds.includes(driver.id)
    )
  },

  // Utility functions (unchanged)
  getDriverById: (id: string) => {
    const state = get()
    return state.drivers.find(d => d.id === id) || 
           state.mergedDrivers.find(d => d.id === id)
  },

  getDriversByPlatform: (platform: Platform) => {
    const state = get()
    return state.drivers.filter(driver => driver.platform === platform)
  },

  getTotalEarnings: () => {
    const state = get()
    const allDrivers = [...state.drivers, ...state.mergedDrivers]
    
    return allDrivers.reduce((totals, driver) => ({
      gross: totals.gross + driver.grossEarnings,
      net: totals.net + driver.netEarnings,
      tips: totals.tips + driver.tips
    }), { gross: 0, net: 0, tips: 0 })
  }
}))
